import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    ShieldQuestion, ArrowLeft, RotateCcw, GitMerge, CheckSquare, Square,
    Sparkles, ExternalLink,
} from "lucide-react";
import { AlertCircle } from "lucide-react";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
    AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

/**
 * Fase 8.3 · Hub "Segnalazioni in attesa"
 * Tabs:
 *  - Duplicati sospetti (aperti / ignorati con undo)
 *  - Incoerenze dati (link al pannello dedicato /admin/data-quality)
 *  - Dati AI in attesa (vini con ai_proposed pendente)
 */
const TABS = [
    { key: "duplicates", label: "Duplicati sospetti" },
    { key: "data_quality", label: "Incoerenze dati" },
    { key: "ai_proposed", label: "Dati AI in attesa" },
];

export default function AdminReviewHub() {
    const [tab, setTab] = useState("duplicates");
    const [counts, setCounts] = useState({
        duplicates_open: 0, data_quality_open: 0, ai_proposed_pending: 0,
        pending_review: 0, total: 0,
    });

    const reloadCounts = useCallback(() => {
        api.get("/admin/review-counts")
            .then(({ data }) => setCounts(data))
            .catch(() => {});
    }, []);
    useEffect(() => { reloadCounts(); }, [reloadCounts]);

    return (
        <div className="max-w-6xl" data-testid="admin-review-hub">
            <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                <h1 className="text-2xl flex items-center gap-2">
                    <ShieldQuestion className="w-5 h-5" strokeWidth={1.5} />
                    Segnalazioni in attesa
                </h1>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    Duplicati sospetti · incoerenze dati · dati AI in attesa
                </p>
            </div>

            <div className="flex gap-2 border-b border-[hsl(var(--border))] mb-6 overflow-x-auto">
                {TABS.map((t) => {
                    const count = t.key === "duplicates" ? counts.duplicates_open
                        : t.key === "data_quality" ? counts.data_quality_open
                        : counts.ai_proposed_pending;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setTab(t.key)}
                            className={`px-4 py-2 text-sm border-b-2 transition-colors whitespace-nowrap ${
                                tab === t.key
                                    ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]"
                                    : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
                            }`}
                            data-testid={`review-tab-${t.key}`}
                        >
                            {t.label}
                            {count > 0 && (
                                <span
                                    className="ml-2 inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[10px] font-semibold"
                                    data-testid={`review-tab-badge-${t.key}`}
                                >
                                    {count > 99 ? "99+" : count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {tab === "duplicates" && <DuplicatesTab onChange={reloadCounts} />}
            {tab === "data_quality" && <DataQualityTab count={counts.data_quality_open} />}
            {tab === "ai_proposed" && <AIProposedTab onChange={reloadCounts} />}
        </div>
    );
}

// ============================================================
// TAB 1: Duplicati sospetti
// ============================================================
function DuplicatesTab({ onChange }) {
    const [view, setView] = useState("open");  // "open" | "dismissed"
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(new Set());

    const load = useCallback(() => {
        setLoading(true);
        api.get(`/admin/duplicate-suspects?status=${view}&limit=200`)
            .then(({ data }) => {
                setItems(data.items || []);
                setSelected(new Set());
            })
            .catch(() => toast.error("Errore caricamento segnalazioni"))
            .finally(() => setLoading(false));
    }, [view]);
    useEffect(() => { load(); }, [load]);

    const toggle = (id) => {
        setSelected((s) => {
            const next = new Set(s);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        if (selected.size === items.length) setSelected(new Set());
        else setSelected(new Set(items.map((x) => x.id)));
    };

    const doAction = async (endpoint, successMsg) => {
        const ids = Array.from(selected);
        if (!ids.length) return;
        try {
            await Promise.all(ids.map((id) => api.post(`/admin/duplicate-suspects/${id}/${endpoint}`)));
            toast.success(`${successMsg} · ${ids.length}`);
            load();
            onChange?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore azione");
        }
    };

    const dismissOne = async (id) => {
        try {
            await api.post(`/admin/duplicate-suspects/${id}/dismiss`);
            toast.success("Segnalazione ignorata");
            load();
            onChange?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        }
    };
    const restoreOne = async (id) => {
        try {
            await api.post(`/admin/duplicate-suspects/${id}/restore`);
            toast.success("Segnalazione ripristinata");
            load();
            onChange?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        }
    };
    const mergeOne = async (id) => {
        try {
            const { data } = await api.post(`/admin/duplicate-suspects/${id}/merge`);
            toast.success("Proposta merge creata — apri il pannello Merge per confermare");
            load();
            onChange?.();
            return data;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore merge");
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => setView("open")}
                        className={`text-xs px-3 py-1.5 rounded-sm border ${
                            view === "open"
                                ? "border-[hsl(var(--primary))] bg-[#FDFBF7] text-[hsl(var(--primary))]"
                                : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"
                        }`}
                        data-testid="dup-view-open"
                    >
                        Aperti
                    </button>
                    <button
                        type="button"
                        onClick={() => setView("dismissed")}
                        className={`text-xs px-3 py-1.5 rounded-sm border ${
                            view === "dismissed"
                                ? "border-[hsl(var(--primary))] bg-[#FDFBF7] text-[hsl(var(--primary))]"
                                : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"
                        }`}
                        data-testid="dup-view-dismissed"
                    >
                        Ignorati (undo)
                    </button>
                </div>
                {selected.size > 0 && (
                    <div className="flex gap-2">
                        <span className="text-xs text-[hsl(var(--muted-foreground))] self-center">
                            {selected.size} selezionat{selected.size === 1 ? "o" : "i"}
                        </span>
                        {view === "open" ? (
                            <button
                                type="button"
                                onClick={() => doAction("dismiss", "Segnalazioni ignorate")}
                                className="co-btn-ghost text-xs"
                                data-testid="dup-batch-dismiss"
                            >
                                Ignora selezionati
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => doAction("restore", "Segnalazioni ripristinate")}
                                className="co-btn-ghost text-xs"
                                data-testid="dup-batch-restore"
                            >
                                <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                                Ripristina selezionati
                            </button>
                        )}
                    </div>
                )}
            </div>

            {loading && <div className="text-sm text-[hsl(var(--muted-foreground))]">Caricamento…</div>}
            {!loading && items.length === 0 && (
                <div className="text-sm text-[hsl(var(--muted-foreground))] py-8 text-center">
                    Nessuna segnalazione {view === "open" ? "aperta" : "ignorata"}.
                </div>
            )}

            {!loading && items.length > 0 && (
                <div className="bg-white border border-[hsl(var(--border))] rounded-sm overflow-hidden">
                    <div className="px-3 py-2 border-b border-[hsl(var(--border))] flex items-center gap-3 bg-[#FAF7F2]">
                        <button
                            type="button"
                            onClick={toggleAll}
                            className="text-[hsl(var(--muted-foreground))]"
                            data-testid="dup-select-all"
                        >
                            {selected.size === items.length ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                        </button>
                        <span className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                            {items.length} segnalazion{items.length === 1 ? "e" : "i"}
                        </span>
                    </div>
                    <ul>
                        {items.map((it) => (
                            <li
                                key={it.id}
                                className="px-3 py-3 border-b border-[hsl(var(--border))] last:border-b-0 flex items-start gap-3"
                                data-testid={`dup-row-${it.id}`}
                            >
                                <button
                                    type="button"
                                    onClick={() => toggle(it.id)}
                                    className="mt-1 text-[hsl(var(--muted-foreground))]"
                                    data-testid={`dup-select-${it.id}`}
                                >
                                    {selected.has(it.id) ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                </button>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <MatchBadge type={it.match_type} similarity={it.similarity} />
                                        <span className="text-xs text-[hsl(var(--muted-foreground))]">
                                            {it.reason}
                                        </span>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-2 text-sm">
                                        <WineSnippet
                                            label="Nuovo"
                                            snap={it.wine_new_snapshot}
                                            fallback={{
                                                name: it.name_new, producer: it.producer_new,
                                                vintage: it.vintage_new,
                                            }}
                                        />
                                        <WineSnippet
                                            label="Esistente"
                                            snap={it.wine_existing_snapshot}
                                            fallback={{
                                                name: it.name_existing, producer: it.producer_existing,
                                                vintage: it.vintage_existing,
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1 shrink-0">
                                    {view === "open" ? (
                                        <>
                                            <MergeConfirm onConfirm={() => mergeOne(it.id)} />
                                            <button
                                                type="button"
                                                onClick={() => dismissOne(it.id)}
                                                className="co-btn-ghost text-xs"
                                                data-testid={`dup-dismiss-${it.id}`}
                                            >
                                                Ignora
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => restoreOne(it.id)}
                                            className="co-btn-ghost text-xs"
                                            data-testid={`dup-restore-${it.id}`}
                                        >
                                            <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                                            Ripristina
                                        </button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function MatchBadge({ type, similarity }) {
    const map = {
        exact: { label: "IDENTICO", cls: "bg-[#5B1C2C] text-white border-[#5B1C2C]" },
        edit_distance: { label: "MOLTO SIMILE", cls: "bg-[#F4E9DC] text-[#5B1C2C] border-[#D9B98F]" },
        partial: { label: "SIMILE", cls: "bg-[#F2EDE4] text-[hsl(var(--muted-foreground))] border-[#D9B98F]" },
    };
    const m = map[type] || map.partial;
    return (
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${m.cls}`}>
            {m.label} · sim {Math.round((similarity || 0) * 100)}%
        </span>
    );
}

function WineSnippet({ label, snap, fallback }) {
    const w = snap || fallback || {};
    return (
        <div className="p-2 bg-[hsl(var(--secondary))] rounded-sm">
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{label}</div>
            <div className="text-sm truncate">{w.name || "—"}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                {w.producer || "—"}{w.vintage ? ` · ${w.vintage}` : ""}
            </div>
            {snap?.id && (
                <Link
                    to={`/admin/vini/${snap.id}`}
                    className="text-xs text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1 mt-1"
                    data-testid={`dup-snippet-link-${snap.id}`}
                >
                    <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                    Apri scheda
                </Link>
            )}
        </div>
    );
}

function MergeConfirm({ onConfirm }) {
    return (
        <AlertDialog>
            <AlertDialogTrigger asChild>
                <button type="button" className="co-btn-ghost text-xs" data-testid="dup-merge-btn">
                    <GitMerge className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Apri merge
                </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Apri proposta di merge</AlertDialogTitle>
                    <AlertDialogDescription>
                        Verrà creata una proposta di merge nel pannello dedicato.
                        Il sistema non unirà nulla in automatico: potrai confermare
                        o rifiutare la fusione da <b>Merge Proposals</b>.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="dup-merge-cancel">Annulla</AlertDialogCancel>
                    <AlertDialogAction onClick={onConfirm} data-testid="dup-merge-confirm">
                        Crea proposta merge
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

// ============================================================
// TAB 2: Incoerenze dati (delega a /admin/data-quality)
// ============================================================
function DataQualityTab({ count }) {
    return (
        <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-6 text-center">
            <AlertCircle className="w-10 h-10 mx-auto text-[hsl(var(--primary))] mb-3" strokeWidth={1.2} />
            <h2 className="text-lg mb-2">
                {count > 0 ? `${count} incoerenz${count === 1 ? "a" : "e"} da rivedere` : "Nessuna incoerenza aperta"}
            </h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-lg mx-auto mb-4">
                Le incoerenze dati (produttore ↔ paese ↔ regione ↔ vitigni ↔ denominazione) sono
                gestite nel pannello dedicato "Qualità dati" con dialog conferma prima → dopo,
                grounding web e apply selettivo.
            </p>
            <Link
                to="/admin/data-quality"
                className="co-btn-primary text-sm"
                data-testid="review-goto-dq"
            >
                <ArrowLeft className="w-4 h-4 rotate-180" strokeWidth={1.5} />
                Vai a Qualità dati
            </Link>
        </div>
    );
}

// ============================================================
// TAB 3: Dati AI in attesa (vini con ai_proposed)
// ============================================================
function AIProposedTab({ onChange }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        api.get("/admin/ai-proposed-wines?limit=100")
            .then(({ data }) => setItems(data.items || []))
            .catch(() => toast.error("Errore caricamento"))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    return (
        <div>
            {loading && <div className="text-sm text-[hsl(var(--muted-foreground))]">Caricamento…</div>}
            {!loading && items.length === 0 && (
                <div className="text-sm text-[hsl(var(--muted-foreground))] py-8 text-center">
                    Nessun vino con dati AI in attesa di approvazione.
                </div>
            )}
            {!loading && items.length > 0 && (
                <ul className="space-y-2">
                    {items.map((w) => (
                        <li
                            key={w.id}
                            className="bg-white border border-[hsl(var(--border))] rounded-sm p-3 flex items-center gap-3"
                            data-testid={`aip-row-${w.id}`}
                        >
                            <Sparkles className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm truncate">{w.name}</div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                                    {w.producer || "—"}
                                    {w.vintage ? ` · ${w.vintage}` : ""}
                                    <span className="ml-2">· fonte {w.source}</span>
                                    <span className="ml-2">· stato {w.status}</span>
                                </div>
                                <div className="text-xs mt-1">
                                    Campi proposti: {Object.keys((w.ai_proposed || {}).data || {}).filter((k) => {
                                        const v = w.ai_proposed?.data?.[k];
                                        return v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
                                    }).join(", ") || "—"}
                                </div>
                            </div>
                            <Link
                                to={`/admin/vini/${w.id}`}
                                className="co-btn-ghost text-xs shrink-0"
                                data-testid={`aip-open-${w.id}`}
                            >
                                <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                                Rivedi
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
